import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  fetchTicketMessages,
  createTicketMessage,
  replyToTicketMessage,
  closeTicketMessage,
  previewMessageApproval,
  approveTicketMessage,
} from '../services/api';
import './TicketChatPanel.css';

const MESSAGE_TYPES = [
  'General', 'Schedule', 'Scope Update', 'Change Appointment', 'Cancellation',
  'Parts Request', 'Due Date Change Request', 'Tracking Update', 'Floor Plan',
  'Support', 'Pricing Estimate', 'Request', 'Missed Appointment', 'Incomplete Scope',
  'Site Prep Issue', 'MFG/Hardware Issues', 'Shipping Damage', 'Warranty Request',
  'Exception', 'Parts Missing', 'Billing Dispute', 'Pay', 'Reminder', 'Job On-Hold',
  'Shipping', 'Other',
];

const URGENCY_NORMAL = 'Normal – Standard priority / routine handling';
const URGENCY_CRITICAL = 'Critical – Immediate action required';

function sideOf(entry, message, viewerName) {
  const author = entry.author || '';
  if (!author) return 'sys';
  const rep = message.repName || '';
  if (/automation/i.test(author)) return (rep && entry.body.includes(rep)) ? 'in' : 'out';
  if (/\(.+\)$/.test(author) && !/PDT|PST|EDT|EST/.test(author)) return 'in';
  if (viewerName && author.includes(viewerName)) return 'out';
  if (rep && author.includes(rep)) return 'in';
  return 'out';
}

export default function TicketChatPanel({
  tickets,
  canEdit,
  onClose,
  onThreadClosed,
  onThreadCreated,
  fieldReps = [],
  variant = 'popup',
  screenPoint = null,
}) {
  const [activeTicketId, setActiveTicketId] = useState(tickets[0]?.recordId || null);
  const [messages, setMessages] = useState([]);
  const [activeRid, setActiveRid] = useState(null);
  const [viewerName, setViewerName] = useState('');
  const [draft, setDraft] = useState('');
  const [composing, setComposing] = useState(false);
  const [newType, setNewType] = useState('General');
  const [urgency, setUrgency] = useState(URGENCY_NORMAL);
  const [notifySupport, setNotifySupport] = useState(false);
  const [chosenRep, setChosenRep] = useState(null);
  const [repQuery, setRepQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [approvePreview, setApprovePreview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');
  const logRef = useRef(null);
  const panelRef = useRef(null);
  const [anchorBox, setAnchorBox] = useState(null);
  const embedded = variant === 'embedded';
  const anchored = !embedded && !!screenPoint;

  const activeTicket = tickets.find(t => t.recordId === activeTicketId) || tickets[0];
  const activeMessage = messages.find(m => m.rid === activeRid) || messages[0] || null;

  const ticketKey = tickets.map(t => t.recordId).join(',');
  useEffect(() => {
    setActiveTicketId(prev => (
      tickets.some(t => String(t.recordId) === String(prev)) ? prev : (tickets[0]?.recordId || null)
    ));
    // Reset only when the set of tickets changes, not when a count updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketKey]);

  useEffect(() => {
    if (!activeTicketId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDraft('');
    setComposing(false);
    setNewType('General');
    setUrgency(URGENCY_NORMAL);
    setNotifySupport(false);
    setRepQuery('');
    const ticket = tickets.find(t => String(t.recordId) === String(activeTicketId));
    const presetId = parseInt(ticket?.relatedFieldRepId, 10);
    setChosenRep(presetId ? { id: presetId, name: ticket.fieldRepName || '' } : null);
    setConfirmClose(false);
    setApprovePreview(null);
    fetchTicketMessages(activeTicketId)
      .then(data => {
        if (cancelled) return;
        const rows = data.messages || [];
        setViewerName(data.viewerName || '');
        setMessages(rows);
        setActiveRid(rows[0]?.rid || null);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load messages');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // tickets is read only to preset the assigned rep; the parent passes a new array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTicketId]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [activeMessage, loading]);

  useLayoutEffect(() => {
    if (!anchored || !screenPoint) {
      setAnchorBox(null);
      return;
    }
    const el = panelRef.current;
    if (!el) return;
    const panelW = el.offsetWidth || 360;
    const panelH = el.offsetHeight || 420;
    const { x, y, width, height } = screenPoint;
    const gap = 14;
    let left = x + gap;
    let top = y - gap - panelH;
    if (left + panelW > width - 8) left = x - gap - panelW;
    if (top < 8) top = 8;
    if (left < 8) left = 8;
    if (top + panelH > height - 8) top = Math.max(8, height - panelH - 8);
    if (left + panelW > width - 8) left = Math.max(8, width - panelW - 8);
    setAnchorBox(prev => (
      prev && Math.abs(prev.left - left) < 1 && Math.abs(prev.top - top) < 1
        ? prev
        : { left, top }
    ));
  }, [anchored, screenPoint, loading, messages.length, activeRid]);

  async function reload() {
    const data = await fetchTicketMessages(activeTicketId);
    const rows = data.messages || [];
    setViewerName(data.viewerName || '');
    setMessages(rows);
    setActiveRid(prev => (rows.some(m => m.rid === prev) ? prev : (rows[0]?.rid || null)));
    return rows;
  }

  async function finishApproved() {
    onThreadClosed?.(activeTicketId);
    const rows = await reload();
    if (!rows.length) onClose?.();
  }

  async function handleReview() {
    if (!activeMessage?.request || reviewing || approving) return;
    if (activeMessage.request.action === 'assign') {
      setApproving(true);
      setError('');
      try {
        await approveTicketMessage(activeTicketId, activeMessage.rid);
        setApprovePreview(null);
        await finishApproved();
      } catch (err) {
        setError(err.message || 'Failed to assign rep');
      } finally {
        setApproving(false);
      }
      return;
    }
    setReviewing(true);
    setError('');
    try {
      const preview = await previewMessageApproval(activeTicketId, activeMessage.rid);
      setApprovePreview(preview);
    } catch (err) {
      setError(err.message || 'Failed to review request');
    } finally {
      setReviewing(false);
    }
  }

  async function handleApprove() {
    if (!activeMessage || approving) return;
    setApproving(true);
    setError('');
    try {
      await approveTicketMessage(activeTicketId, activeMessage.rid);
      setApprovePreview(null);
      await finishApproved();
    } catch (err) {
      setError(err.message || 'Failed to approve request');
    } finally {
      setApproving(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending || !activeTicketId) return;
    if (!chosenRep?.id) {
      setError('Pick a field rep');
      return;
    }
    setSending(true);
    setError('');
    try {
      const result = await createTicketMessage(activeTicketId, {
        text,
        type: newType,
        urgency,
        repId: chosenRep.id,
        repName: chosenRep.name,
        notifySupport,
      });
      setDraft('');
      setComposing(false);
      onThreadCreated?.(activeTicketId);
      await reload();
      if (result.rid) setActiveRid(result.rid);
    } catch (err) {
      setError(err.message || 'Failed to create message');
    } finally {
      setSending(false);
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !activeMessage || sending) return;
    setSending(true);
    setError('');
    try {
      await replyToTicketMessage(activeTicketId, activeMessage.rid, text);
      setDraft('');
      await reload();
    } catch (err) {
      setError(err.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  }

  async function handleCloseThread() {
    if (!activeMessage || closing) return;
    if (!confirmClose) {
      setConfirmClose(true);
      return;
    }
    setClosing(true);
    setError('');
    try {
      await closeTicketMessage(activeTicketId, activeMessage.rid);
      onThreadClosed?.(activeTicketId);
      const rows = await reload();
      if (!rows.length) onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to close message');
    } finally {
      setClosing(false);
      setConfirmClose(false);
    }
  }

  const title = activeTicket?.siteName || `Ticket #${activeTicketId}`;

  const className = [
    'ticket-chat',
    embedded ? 'embedded' : '',
    anchored ? 'anchored' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={panelRef}
      className={className}
      role="dialog"
      aria-label="Ticket messages"
      style={anchored ? {
        left: anchorBox ? anchorBox.left : 0,
        top: anchorBox ? anchorBox.top : 0,
        visibility: anchorBox ? 'visible' : 'hidden',
      } : undefined}
    >
      {!embedded && (
      <div className="ticket-chat-header">
        <div className="ticket-chat-title">
          <span className="ticket-chat-kicker">Messages</span>
          <a
            href={`https://siteservicesnow.quickbase.com/nav/app/bjt99j3pb/table/bjwdgf796/action/dr?rid=${activeTicketId}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open ticket in Quickbase"
          >
            {title}
          </a>
          <span className="ticket-chat-id">#{activeTicketId}</span>
        </div>
        <button type="button" className="ticket-chat-x" onClick={onClose} aria-label="Close chat">
          ×
        </button>
      </div>
      )}

      {tickets.length > 1 && (
        <div className="ticket-chat-switch">
          {tickets.map(t => (
            <button
              key={t.recordId}
              type="button"
              className={t.recordId === activeTicketId ? 'on' : ''}
              onClick={() => setActiveTicketId(t.recordId)}
            >
              #{t.recordId}
              {t.openCount > 0 ? ` · ${t.openCount}` : ''}
            </button>
          ))}
        </div>
      )}

      {messages.length > 1 && (
        <div className="ticket-chat-switch">
          {messages.map(m => (
            <button
              key={m.rid}
              type="button"
              className={m.rid === activeMessage?.rid ? 'on' : ''}
              onClick={() => { setActiveRid(m.rid); setConfirmClose(false); setApprovePreview(null); }}
            >
              {m.type}
            </button>
          ))}
        </div>
      )}

      <div className="ticket-chat-log" ref={logRef}>
        {loading && <div className="ticket-chat-empty">Loading…</div>}
        {!loading && error && !messages.length && <div className="ticket-chat-error">{error}</div>}
        {!loading && !error && !activeMessage && (
          <div className="ticket-chat-empty">No open messages on this ticket.</div>
        )}
        {!loading && activeMessage && activeMessage.entries.length === 0 && (
          <div className="ticket-chat-empty">This thread has no text yet.</div>
        )}
        {!loading && activeMessage && activeMessage.entries.map((entry, i) => {
          const side = sideOf(entry, activeMessage, viewerName);
          const when = [entry.date, entry.time, entry.tz].filter(Boolean).join(' ');
          return (
            <div key={`${activeMessage.rid}-${i}`} className={`ticket-chat-bub ${side}`}>
              <div className="ticket-chat-meta">
                {entry.author || 'System'}
                {when ? ` · ${when}` : ''}
              </div>
              <div className="ticket-chat-txt">{entry.body}</div>
            </div>
          );
        })}
      </div>

      {error && messages.length > 0 && <div className="ticket-chat-error">{error}</div>}

      {activeMessage?.request && (
        <div className="ticket-chat-request">
          <div className="ticket-chat-request-title">{activeMessage.request.title}</div>
          {!approvePreview && <p>{activeMessage.request.summary}</p>}
          {approvePreview && (
            <div className="ticket-chat-request-preview">
              <strong>{approvePreview.title}</strong>
              {approvePreview.lines?.map(line => <p key={line}>{line}</p>)}
              {approvePreview.warning && <p className="warn">{approvePreview.warning}</p>}
            </div>
          )}
          {canEdit && (
            <div className="ticket-chat-request-actions">
              {approvePreview ? (
                <>
                  <button type="button" className="ghost" onClick={() => setApprovePreview(null)} disabled={approving}>
                    Cancel
                  </button>
                  <button type="button" className="approve" onClick={handleApprove} disabled={approving}>
                    {approving ? 'Approving…' : (approvePreview.confirmLabel || 'Approve')}
                  </button>
                </>
              ) : (
                <button type="button" className="approve" onClick={handleReview} disabled={reviewing || approving}>
                  {reviewing ? 'Reviewing…' : approving ? 'Assigning…' : activeMessage.request.button}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {canEdit && !loading && (composing || !activeMessage) && (
        <form className="ticket-chat-composer" onSubmit={handleCreate}>
          <div className="ticket-chat-composer-bar">
            <select
              className="ticket-chat-type"
              value={newType}
              onChange={e => setNewType(e.target.value)}
              disabled={sending}
            >
              {MESSAGE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
            <select
              className="ticket-chat-type"
              value={urgency}
              onChange={e => setUrgency(e.target.value)}
              disabled={sending}
            >
              <option value={URGENCY_NORMAL}>Normal</option>
              <option value={URGENCY_CRITICAL}>Critical</option>
            </select>
            {composing && (
              <button type="button" className="ticket-chat-close-btn" onClick={() => setComposing(false)} disabled={sending}>
                Cancel
              </button>
            )}
          </div>
          {chosenRep ? (
            <div className="ticket-chat-rep-chip">
              {chosenRep.name || `Rep #${chosenRep.id}`}
              <button type="button" onClick={() => setChosenRep(null)} disabled={sending} aria-label="Clear field rep">×</button>
            </div>
          ) : (
            <div className="ticket-chat-rep-search">
              <input
                value={repQuery}
                placeholder="Field rep name or id…"
                onChange={e => setRepQuery(e.target.value)}
                disabled={sending}
              />
              {repQuery.trim() && (
                <div className="ticket-chat-rep-list">
                  {fieldReps
                    .filter(r => {
                      const q = repQuery.trim().toLowerCase();
                      return (r.name || '').toLowerCase().includes(q) || String(r.repId).includes(q);
                    })
                    .slice(0, 8)
                    .map(r => (
                      <button
                        key={r.repId}
                        type="button"
                        onClick={() => { setChosenRep({ id: r.repId, name: r.name || '' }); setRepQuery(''); }}
                      >
                        {r.name || `Rep #${r.repId}`} <span>#{r.repId}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
          <label className="ticket-chat-support">
            <input
              type="checkbox"
              checked={notifySupport}
              onChange={e => setNotifySupport(e.target.checked)}
              disabled={sending}
            />
            Also notify SSN Support Team
          </label>
          <div className="ticket-chat-composer-row">
            <textarea
              value={draft}
              rows={2}
              placeholder="Write a message…"
              onChange={e => setDraft(e.target.value)}
              disabled={sending}
            />
            <button type="submit" disabled={sending || !draft.trim() || !chosenRep}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
          <div className="ticket-chat-hint">Sends to the field rep, and notifies the FSM and project manager.</div>
        </form>
      )}

      {activeMessage && !(canEdit && composing) && (
        <form className="ticket-chat-composer" onSubmit={handleSend}>
          <div className="ticket-chat-composer-bar">
            <span>{activeMessage.type}</span>
            {canEdit && (
              <button
                type="button"
                className="ticket-chat-new-btn"
                onClick={() => { setComposing(true); setDraft(''); setConfirmClose(false); }}
                disabled={closing || sending}
              >
                New message
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="ticket-chat-close-btn"
                onClick={handleCloseThread}
                disabled={closing || sending}
              >
                {closing ? 'Closing…' : confirmClose ? 'Confirm close' : 'Close thread'}
              </button>
            )}
          </div>
          {canEdit ? (
            <div className="ticket-chat-composer-row">
              <textarea
                value={draft}
                rows={2}
                placeholder="Write a reply…"
                onChange={e => setDraft(e.target.value)}
                disabled={sending}
              />
              <button type="submit" disabled={sending || !draft.trim()}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          ) : (
            <div className="ticket-chat-hint">You can read this thread. Replies are turned off for your account.</div>
          )}
        </form>
      )}
    </div>
  );
}
